import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Clock,
  Mail,
  CheckCircle2,
  MousePointerClick,
  Search,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import type { ReviewRequest, Interaction, Staff } from "@shared/schema";

interface ReviewRequestWithStatus extends ReviewRequest {
  status: "sent" | "clicked" | "reviewed";
  staffName?: string;
}

const ITEMS_PER_PAGE = 10;

export default function History() {
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const { data: requests, isLoading: requestsLoading } = useQuery<ReviewRequest[]>({
    queryKey: ["/api/review-requests"],
  });

  const { data: interactions } = useQuery<Interaction[]>({
    queryKey: ["/api/interactions"],
  });

  const { data: staffList } = useQuery<Staff[]>({
    queryKey: ["/api/staff"],
  });

  // Create a map of review request IDs that have interactions (reviewed)
  const reviewedRequestIds = new Set(
    interactions
      ?.filter((i) => i.reviewRequestId && i.rating)
      .map((i) => i.reviewRequestId) || []
  );

  // Create a map of staff IDs to names
  const staffMap = new Map(staffList?.map((s) => [s.id, s.name]) || []);

  // Add status to each request
  const requestsWithStatus: ReviewRequestWithStatus[] = useMemo(() => {
    return (requests || []).map((request) => {
      let status: "sent" | "clicked" | "reviewed" = "sent";

      if (reviewedRequestIds.has(request.id)) {
        status = "reviewed";
      } else if (request.clickedAt) {
        status = "clicked";
      }

      return {
        ...request,
        status,
        staffName: staffMap.get(request.staffId),
      };
    });
  }, [requests, reviewedRequestIds, staffMap]);

  // Filter by search query
  const filteredRequests = useMemo(() => {
    if (!searchQuery.trim()) return requestsWithStatus;

    const query = searchQuery.toLowerCase();
    return requestsWithStatus.filter(
      (request) =>
        request.customerName.toLowerCase().includes(query) ||
        request.customerEmail.toLowerCase().includes(query) ||
        (request.staffName && request.staffName.toLowerCase().includes(query))
    );
  }, [requestsWithStatus, searchQuery]);

  // Pagination
  const totalPages = Math.ceil(filteredRequests.length / ITEMS_PER_PAGE);
  const paginatedRequests = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredRequests.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredRequests, currentPage]);

  // Reset to page 1 when search changes
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const getStatusBadge = (status: "sent" | "clicked" | "reviewed") => {
    switch (status) {
      case "reviewed":
        return (
          <Badge variant="default" className="bg-green-500 hover:bg-green-600">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            Reviewed
          </Badge>
        );
      case "clicked":
        return (
          <Badge variant="secondary" className="bg-blue-100 text-blue-700 hover:bg-blue-200">
            <MousePointerClick className="h-3 w-3 mr-1" />
            Clicked
          </Badge>
        );
      case "sent":
        return (
          <Badge variant="outline">
            <Mail className="h-3 w-3 mr-1" />
            Sent
          </Badge>
        );
    }
  };

  const isLoading = requestsLoading;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-semibold">Request History</h1>
        <p className="text-muted-foreground mt-1">
          Track all review requests you've sent
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Clock className="h-5 w-5" />
              Recent Requests
              {!isLoading && (
                <Badge variant="secondary" className="ml-2">
                  {filteredRequests.length}
                </Badge>
              )}
            </CardTitle>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : filteredRequests.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Mail className="h-12 w-12 mx-auto mb-4 opacity-50" />
              {searchQuery ? (
                <>
                  <p className="text-lg font-medium">No results found</p>
                  <p className="text-sm">Try a different search term</p>
                </>
              ) : (
                <>
                  <p className="text-lg font-medium">No requests sent yet</p>
                  <p className="text-sm">Send your first review request to see it here</p>
                </>
              )}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Customer</TableHead>
                      <TableHead>Staff</TableHead>
                      <TableHead>Sent</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedRequests.map((request) => (
                      <TableRow key={request.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{request.customerName}</p>
                            <p className="text-sm text-muted-foreground">
                              {request.customerEmail}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm">
                            {request.staffName || "Unknown"}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-muted-foreground">
                            {request.sentAt
                              ? format(new Date(request.sentAt), "MMM d, yyyy")
                              : "—"}
                          </span>
                        </TableCell>
                        <TableCell>{getStatusBadge(request.status)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <p className="text-sm text-muted-foreground">
                    Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1} to{" "}
                    {Math.min(currentPage * ITEMS_PER_PAGE, filteredRequests.length)} of{" "}
                    {filteredRequests.length}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    <span className="text-sm text-muted-foreground px-2">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
